
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { DollarSign, Upload } from "lucide-react";

interface NoteDiscountInputs {
  remainingBalance: number;
  monthlyPayment: number;
  remainingTermMonths: number;
  noteInterestRate: number;
  targetYield: number;
  purchasePrice: number;
  balloonPayment: number;
}

interface NoteDiscountCalculatorProps {
  formatCurrency: (amount: number) => string;
  formatPercentage: (percentage: number) => string;
  onLoadToInvestment?: (data: any) => void;
}

const NoteDiscountCalculator = ({ formatCurrency, formatPercentage, onLoadToInvestment }: NoteDiscountCalculatorProps) => {
  const [inputs, setInputs] = useState<NoteDiscountInputs>({
    remainingBalance: 0,
    monthlyPayment: 0,
    remainingTermMonths: 0,
    noteInterestRate: 0,
    targetYield: 0,
    purchasePrice: 0,
    balloonPayment: 0
  });

  const [results, setResults] = useState<{
    purchasePrice?: number;
    actualYield?: number;
    totalReturn?: number;
  }>({});

  const handleInputChange = (field: keyof NoteDiscountInputs, value: string) => {
    const numValue = parseFloat(value) || 0;
    setInputs(prev => ({
      ...prev,
      [field]: numValue
    }));
  };

  const calculatePurchasePrice = () => {
    const { monthlyPayment, remainingTermMonths, targetYield, balloonPayment } = inputs;
    
    if (monthlyPayment <= 0 || remainingTermMonths <= 0 || targetYield <= 0) {
      return;
    }

    const monthlyYield = (targetYield / 100) / 12;
    
    // Present value of monthly payments
    let pvPayments = 0;
    if (monthlyYield > 0) {
      pvPayments = monthlyPayment * ((1 - Math.pow(1 + monthlyYield, -remainingTermMonths)) / monthlyYield);
    } else {
      pvPayments = monthlyPayment * remainingTermMonths;
    }
    
    // Present value of balloon payment
    const pvBalloon = balloonPayment > 0 ? balloonPayment / Math.pow(1 + monthlyYield, remainingTermMonths) : 0;
    
    const totalPV = pvPayments + pvBalloon;
    const totalReturn = (monthlyPayment * remainingTermMonths) + balloonPayment;
    
    console.log('Purchase Price Calculation:', {
      monthlyPayment,
      remainingTermMonths,
      targetYield,
      monthlyYield,
      balloonPayment,
      pvPayments,
      pvBalloon,
      totalPV,
      totalReturn
    });
    
    setResults({
      purchasePrice: totalPV,
      totalReturn: totalReturn
    });
  };

  const calculateYield = () => {
    const { monthlyPayment, remainingTermMonths, purchasePrice, balloonPayment } = inputs;
    
    if (monthlyPayment <= 0 || remainingTermMonths <= 0 || purchasePrice <= 0) {
      return;
    }

    // Use iterative method to find yield
    let lowYield = 0.001; // 0.1%
    let highYield = 0.5;   // 50%
    const tolerance = 0.0001;
    const maxIterations = 100;
    
    for (let i = 0; i < maxIterations; i++) {
      const midYield = (lowYield + highYield) / 2;
      const monthlyYield = midYield / 12;
      
      // Calculate present value with current yield guess
      let pvPayments = 0;
      if (monthlyYield > 0) {
        pvPayments = monthlyPayment * ((1 - Math.pow(1 + monthlyYield, -remainingTermMonths)) / monthlyYield);
      } else {
        pvPayments = monthlyPayment * remainingTermMonths;
      }
      
      const pvBalloon = balloonPayment > 0 ? balloonPayment / Math.pow(1 + monthlyYield, remainingTermMonths) : 0;
      const totalPV = pvPayments + pvBalloon;
      
      if (Math.abs(totalPV - purchasePrice) < tolerance) {
        const totalReturn = (monthlyPayment * remainingTermMonths) + balloonPayment;
        console.log('Yield Calculation Result:', {
          monthlyPayment,
          remainingTermMonths,
          purchasePrice,
          balloonPayment,
          pvPayments,
          pvBalloon,
          totalPV,
          actualYield: midYield * 100,
          totalReturn
        });
        setResults({
          actualYield: midYield * 100,
          totalReturn: totalReturn
        });
        return;
      }
      
      if (totalPV > purchasePrice) {
        lowYield = midYield;
      } else {
        highYield = midYield;
      }
      
      if (Math.abs(highYield - lowYield) < tolerance) {
        break;
      }
    }
    
    const finalYield = ((lowYield + highYield) / 2) * 100;
    const totalReturn = (monthlyPayment * remainingTermMonths) + balloonPayment;
    console.log('Final Yield Calculation:', {
      finalYield,
      totalReturn
    });
    setResults({
      actualYield: finalYield,
      totalReturn: totalReturn
    });
  };

  const handleLoadToInvestment = () => {
    if (!onLoadToInvestment) return;
    
    const investmentData = {
      monthly_payment: inputs.monthlyPayment || 0,
      remaining_balance: inputs.remainingBalance || 0,
      balloon_payment_amount: inputs.balloonPayment || 0,
      has_balloon_payment: (inputs.balloonPayment || 0) > 0,
      term_remaining: inputs.remainingTermMonths ? `${inputs.remainingTermMonths} months` : '',
      asking_price: results.purchasePrice || inputs.purchasePrice || 0,
      investor_yield: results.actualYield ? `${results.actualYield.toFixed(2)}%` : inputs.targetYield ? `${inputs.targetYield}%` : '',
      original_interest_rate: inputs.noteInterestRate || 0
    };
    
    onLoadToInvestment(investmentData);
  };

  const hasInputsOrResults = inputs.monthlyPayment > 0 || inputs.remainingBalance > 0 || results.purchasePrice || results.actualYield;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <DollarSign className="h-5 w-5 text-blue-600" />
          <span className="text-sm font-medium text-blue-800">Calculate note purchase price or yield</span>
        </div>
        {hasInputsOrResults && onLoadToInvestment && (
          <Button
            onClick={handleLoadToInvestment}
            size="sm"
            className="bg-green-600 hover:bg-green-700"
          >
            <Upload className="h-4 w-4 mr-1" />
            Load to Investment
          </Button>
        )}
      </div>

      <div className="space-y-3">
        <div className="space-y-1">
          <Label className="text-sm">Monthly Payment</Label>
          <Input
            type="number"
            step="0.01"
            value={inputs.monthlyPayment || ''}
            onChange={(e) => handleInputChange('monthlyPayment', e.target.value)}
            placeholder="0.00"
            className="text-sm"
          />
        </div>
        
        <div className="space-y-1">
          <Label className="text-sm">Remaining Term (Months)</Label>
          <Input
            type="number"
            value={inputs.remainingTermMonths || ''}
            onChange={(e) => handleInputChange('remainingTermMonths', e.target.value)}
            placeholder="Months remaining"
            className="text-sm"
          />
        </div>
        
        <div className="space-y-1">
          <Label className="text-sm">Balloon Payment (if any)</Label>
          <Input
            type="number"
            step="0.01"
            value={inputs.balloonPayment || ''}
            onChange={(e) => handleInputChange('balloonPayment', e.target.value)}
            placeholder="0.00"
            className="text-sm"
          />
        </div>
        
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="text-sm">Target Yield (%)</Label>
            <Input
              type="number"
              step="0.01"
              value={inputs.targetYield || ''}
              onChange={(e) => handleInputChange('targetYield', e.target.value)}
              placeholder="e.g., 12"
              className="text-sm"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-sm">OR Purchase Price</Label>
            <Input
              type="number"
              step="0.01"
              value={inputs.purchasePrice || ''}
              onChange={(e) => handleInputChange('purchasePrice', e.target.value)}
              placeholder="0.00"
              className="text-sm"
            />
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-2">
          <Button 
            onClick={calculatePurchasePrice} 
            size="sm" 
            className="w-full"
            disabled={!inputs.targetYield || !inputs.monthlyPayment || !inputs.remainingTermMonths}
          >
            Calculate Purchase Price
          </Button>
          <Button 
            onClick={calculateYield} 
            size="sm" 
            variant="outline" 
            className="w-full"
            disabled={!inputs.purchasePrice || !inputs.monthlyPayment || !inputs.remainingTermMonths}
          >
            Calculate Yield
          </Button>
        </div>
      </div>
      
      {(results.purchasePrice !== undefined || results.actualYield !== undefined) && (
        <div className="space-y-2 pt-3 border-t border-gray-200">
          {results.purchasePrice !== undefined && (
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">Purchase Price for {inputs.targetYield}% yield:</span>
              <span className="text-sm font-bold text-blue-700">
                {formatCurrency(results.purchasePrice)}
              </span>
            </div>
          )}
          {results.actualYield !== undefined && (
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">Yield at {formatCurrency(inputs.purchasePrice)}:</span>
              <span className="text-sm font-bold text-blue-700">
                {formatPercentage(results.actualYield)}
              </span>
            </div>
          )}
          {results.totalReturn !== undefined && (
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">Total Cash Flow:</span>
              <span className="text-sm font-bold text-blue-700">
                {formatCurrency(results.totalReturn)}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NoteDiscountCalculator;
